import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, X } from 'lucide-react';
import api from '../services/api';
import { useNotificationContext } from '../contexts/NotificationContext';

const PRIORIDADES = ['Baja', 'Media', 'Alta', 'Crítica'];
const CAMPO = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500';

// Boton + modal para que el staff/admin edite los detalles del ticket: titulo, categoria/subcategoria,
// prioridad y solicitante. El cambio queda anotado en el ticket como comentario interno.
export default function TicketEditarDetalles({ ticket, onGuardado }) {
  const { alertSuccess, alertError } = useNotificationContext();
  const [abierto, setAbierto] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({});

  const categoriaActual = ticket.subcategoria?.categoriaId ?? ticket.subcategoria?.categoria?.id ?? '';

  const abrir = () => {
    setForm({
      descripcion: ticket.descripcion || '',
      categoriaId: categoriaActual,
      subcategoriaId: ticket.subcategoriaId ?? ticket.subcategoria?.id ?? '',
      prioridad: ticket.prioridad || 'Media',
      nombreSolicitante: ticket.nombreSolicitante || '',
      emailSolicitante: ticket.emailSolicitante || '',
    });
    setAbierto(true);
  };

  useEffect(() => {
    if (!abierto || categorias.length) return;
    api.get('/categoriasticket').then((r) => setCategorias(r.data || [])).catch(() => alertError('No se pudieron cargar las categorías.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  useEffect(() => {
    if (!abierto || !form.categoriaId) { setSubcategorias([]); return; }
    api.get(`/categoriasticket/${form.categoriaId}/subcategorias`).then((r) => setSubcategorias(r.data || [])).catch(() => setSubcategorias([]));
  }, [abierto, form.categoriaId]);

  useEffect(() => {
    if (!abierto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [abierto]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const cambiarCategoria = (e) => setForm((f) => ({ ...f, categoriaId: e.target.value, subcategoriaId: '' }));

  const guardar = async (e) => {
    e.preventDefault();
    if (!form.subcategoriaId) { alertError('Elige la subcategoría.'); return; }
    setGuardando(true);
    try {
      await api.put(`/tickets/${ticket.id}/detalles`, {
        descripcion: form.descripcion,
        subcategoriaId: Number(form.subcategoriaId),
        prioridad: form.prioridad,
        nombreSolicitante: form.nombreSolicitante,
        emailSolicitante: form.emailSolicitante,
      });
      alertSuccess('Detalles del ticket actualizados.');
      setAbierto(false);
      onGuardado?.();
    } catch (err) {
      const m = err.response?.data?.message;
      alertError(Array.isArray(m) ? m.join(' ') : m || 'No se pudieron guardar los cambios.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
      <button
        onClick={abrir}
        className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
        title="Editar título, categoría, prioridad y solicitante"
      >
        <Pencil className="w-4 h-4" />
        Editar detalles
      </button>
      {abierto && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onMouseDown={(e) => { if (e.target === e.currentTarget) setAbierto(false); }}>
          <form onSubmit={guardar} role="dialog" aria-modal="true" aria-label="Editar detalles del ticket" className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Editar detalles · Ticket #{ticket.id}</h2>
              <button type="button" onClick={() => setAbierto(false)} className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-gray-200" htmlFor="ed-desc">Título</label>
                <textarea id="ed-desc" rows={3} maxLength={4000} required value={form.descripcion} onChange={set('descripcion')} className={CAMPO} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-gray-200" htmlFor="ed-cat">Categoría</label>
                  <select id="ed-cat" value={form.categoriaId} onChange={cambiarCategoria} className={CAMPO} required>
                    <option value="">Elegir…</option>
                    {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-gray-200" htmlFor="ed-sub">Subcategoría</label>
                  <select id="ed-sub" value={form.subcategoriaId} onChange={set('subcategoriaId')} className={CAMPO} required disabled={!form.categoriaId}>
                    <option value="">Elegir…</option>
                    {subcategorias.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-gray-200" htmlFor="ed-prio">Prioridad</label>
                <select id="ed-prio" value={form.prioridad} onChange={set('prioridad')} className={CAMPO}>
                  {PRIORIDADES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Cambiar la categoría no cambia la prioridad: ajústala aquí si corresponde.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-gray-200" htmlFor="ed-nom">Solicitante</label>
                  <input id="ed-nom" maxLength={100} required value={form.nombreSolicitante} onChange={set('nombreSolicitante')} className={CAMPO} />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-700 dark:text-gray-200" htmlFor="ed-mail">Correo del solicitante</label>
                  <input id="ed-mail" type="email" maxLength={200} required value={form.emailSolicitante} onChange={set('emailSolicitante')} className={CAMPO} />
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Los cambios quedan anotados en el ticket como comentario interno. Si cambias el correo, el ticket pasa a ser de esa persona.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <button type="button" onClick={() => setAbierto(false)} className="px-4 py-2 text-sm rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">Cancelar</button>
              <button type="submit" disabled={guardando} className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">{guardando ? 'Guardando…' : 'Guardar cambios'}</button>
            </div>
          </form>
        </div>,
        document.body,
      )}
    </>
  );
}
